import { useMutation, useQuery } from '@apollo/client';
import moment from 'moment-timezone';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  BackgroundColor,
  Container,
  Content,
  FormContainer,
  SectionContainer,
  StickyHeader,
  StickyFooter,
  HEADER_FOOTER_HEIGHT
} from './IntakeFormRoute.styled';
import { FormRenderer } from '../components/FormRenderer/FormRenderer';
import Header from '../components/Header/Header';
import { ProgressFooter } from '../components/ProgressFooter/ProgressFooter';
import SectionIndicator from '../components/SectionIndicator';
import { Loader } from 'components/Loader/Loader';
import { usePatient } from 'hooks/usePatient/usePatient';
import { utils, FormSection, IntakeForm, Answer } from 'kb-shared';
import { INTAKE_COMPLETE_FORM, INTAKE_SAVE_ANSWER } from 'kb-shared/graphql/mutations';
import { INTAKE_FORM } from 'kb-shared/graphql/queries';
import { BugTracker } from 'kb-shared/utilities/bugTracker';
import { FormAnswers } from 'kb-shared/utilities/intake_form.helper';
import KBContacts from 'kb-shared/utilities/kindbody_contacts';
import { showErrorToast } from 'utilities/notificationUtils';
import { pageUrl } from 'utilities/pageUrl';

import {
  isFormField,
  scrollToFormElement,
  getQuestionIdsToHide,
  validateDataTypes,
  getNewFormState,
  scrollToQuestion
} from './IntakeFormRoute.helpers';
import {
  FetchIntakeFormResponse,
  FormCompletionResponse,
  FormCompletionVariables,
  FormElement,
  Props,
  SaveAnswerResponse,
  SaveAnswerVariables
} from './IntakeFormRoute.types';

export const IntakeFormRoute = ({ onFormBecomeUnavailable }: Props) => {
  const navigate = useNavigate();
  const [currentFormSectionIndex, setCurrentFormSectionIndex] = useState(0);
  const [currentFormIndex, setCurrentFormIndex] = useState(0);
  const [progress, setProgress] = useState<number | null>(null);
  const [formStructure, setFormStructure] = useState<IntakeForm>({
    id: '',
    title: '',
    formSections: [],
    conditionalFormElements: []
  });
  const [answers, setAnswers] = useState<FormAnswers | null>(null);
  const [invalidQuestions, setInvalidQuestions] = useState<string[]>([]);
  const pendingAnswersRef = useRef<FormAnswers | null>(null);

  const formRendererRef: React.RefObject<FormRenderer> = React.createRef();

  const [completeIntakeForm] = useMutation<FormCompletionResponse, FormCompletionVariables>(
    INTAKE_COMPLETE_FORM
  );
  const [saveAnswer] = useMutation<SaveAnswerResponse, SaveAnswerVariables>(INTAKE_SAVE_ANSWER);

  const isGyn = window.location.search.indexOf('gyn') > -1;
  const patient = usePatient();
  const INTAKE_FORM_ID = utils.intakeFormId(patient.patient?.gender);
  const { loading: loadingForm } = useQuery<FetchIntakeFormResponse>(INTAKE_FORM, {
    variables: { formId: INTAKE_FORM_ID, appointmentType: isGyn ? 'gyn' : null },
    skip: !patient.patient?.gender,
    onError: error => BugTracker.notify(error, 'IntakeFormLoading'),
    onCompleted: formData => {
      let questionsWithInvalidDataType: string[] = [];
      if (formData?.patientForm) {
        questionsWithInvalidDataType = validateDataTypes(formData.patientForm);

        const questions = utils.normalizeIntakeForm(formData.patientForm);
        setFormStructure(questions);

        if (formData?.patientFormCompletion?.answers) {
          setProgress(formData.patientFormCompletion.percentageComplete);
          const questionsAnswers = utils.mapIntakeFormAnswers(
            formData.patientFormCompletion.answers
          );
          pendingAnswersRef.current = questionsAnswers;
          setAnswers(questionsAnswers);
          const newFormState = getNewFormState(
            questions,
            questionsAnswers,
            questionsWithInvalidDataType
          );
          if (newFormState) {
            updateDisplayedForm(newFormState.newSectionIndex, newFormState.newQuestionIndex);
          }
        } else {
          pendingAnswersRef.current = {};
          setAnswers({});
        }

        setInvalidQuestions(questionsWithInvalidDataType);
      }
    }
  });

  useEffect(() => {
    const { formSections } = formStructure;
    const currentSection = formSections[currentFormSectionIndex];
    if (currentSection == null) return;

    const displayedElements = currentSection.formElements.filter(
      element =>
        !getQuestionIdsToHide(
          formStructure.formSections[currentFormSectionIndex],
          formStructure.conditionalFormElements || [],
          answers
        ).includes(element.id)
    );
    if (currentFormIndex > displayedElements.length - 1) return;
    const questionToScrollTo = displayedElements[currentFormIndex];

    if (questionToScrollTo) scrollToQuestion(questionToScrollTo.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFormSectionIndex, currentFormIndex]);

  const updateDisplayedForm = (sectionIndex: number, formIndex: number) => {
    setCurrentFormSectionIndex(sectionIndex);
    setCurrentFormIndex(formIndex);
  };

  const currentSection = (): FormSection | null => {
    return formStructure.formSections[currentFormSectionIndex];
  };

  const updateAnswers = (updater: (previousAnswers: FormAnswers | null) => FormAnswers) => {
    const sourceAnswers = pendingAnswersRef.current ?? answers;
    const nextAnswers = updater(sourceAnswers ?? null);
    pendingAnswersRef.current = nextAnswers;
    setAnswers(nextAnswers);
  };

  const buildOptimisticAnswer = (
    answerValue: string | Array<string> | undefined,
    id: string,
    previousAnswer?: Answer
  ): Answer => {
    return {
      id: previousAnswer?.id ?? `optimistic-${id}`,
      data: Array.isArray(answerValue) ? JSON.stringify(answerValue) : (answerValue ?? ''),
      dataArray: Array.isArray(answerValue) ? answerValue : null,
      formElementId: parseInt(id),
      formElementTitle: previousAnswer?.formElementTitle ?? ''
    };
  };

  const restorePreviousAnswer = (id: string, previousAnswer?: Answer) => {
    updateAnswers(previousAnswers => {
      const nextAnswers = { ...(previousAnswers || {}) };
      if (previousAnswer) {
        nextAnswers[id] = previousAnswer;
      } else {
        delete nextAnswers[id];
      }
      return nextAnswers;
    });
  };

  const isAnswerMissing = (
    formElement: FormElement,
    answersToCheck: FormAnswers | null,
    hiddenQuestionIds: string[]
  ) => {
    if (answersToCheck == null) return true;
    if (hiddenQuestionIds.includes(formElement.id)) {
      // If question which answer is checked is question that needs to be hidden,
      // it is irrelevant if answer is missing for the question.
      return false;
    }

    const answer = answersToCheck[formElement.id];
    return answer == null || answer.data == null || answer.data === '' || answer.data === '[]';
  };

  const getFirstRequiredFormElementMissingAnswer = (currentSection: FormSection | null) => {
    if (currentSection == null) return;

    const answersForValidation = pendingAnswersRef.current ?? answers;

    // Get the list of hidden question IDs to exclude from validation
    const hiddenQuestionIds = getQuestionIdsToHide(
      currentSection,
      formStructure.conditionalFormElements || [],
      answersForValidation
    );

    // Filter for required AND visible questions only
    const formElementsWithRequiredAnswer = currentSection.formElements.filter(
      el => isFormField(el) && el.required && !hiddenQuestionIds.includes(el.id)
    );

    if (formElementsWithRequiredAnswer == null || formElementsWithRequiredAnswer.length === 0) {
      return;
    }

    return formElementsWithRequiredAnswer.find(el =>
      isAnswerMissing(el, answersForValidation, hiddenQuestionIds)
    );
  };

  const notifyAboutMissingAnswer = (formElementMissingAnswer: FormElement) => {
    showErrorToast(
      'To continue, please answer any mandatory questions marked with a red asterisk.'
    );
    scrollToFormElement(formElementMissingAnswer.id);
  };

  const showNextSection = () => {
    const formElementWithMissingAnswer = getFirstRequiredFormElementMissingAnswer(currentSection());
    if (formElementWithMissingAnswer) {
      notifyAboutMissingAnswer(formElementWithMissingAnswer);
      return;
    }

    updateDisplayedForm(currentFormSectionIndex + 1, 0);
  };

  const showPreviousSection = () => {
    updateDisplayedForm(currentFormSectionIndex - 1, 0);
  };

  const calculateProgress = (): number => {
    if (!progress) {
      return 0;
    }
    return progress / 100;
  };

  const submitAnswer = async (answerValue: string | Array<string> | undefined, id: string) => {
    const previousAnswer = (pendingAnswersRef.current ?? answers)?.[id];
    const optimisticAnswer = buildOptimisticAnswer(answerValue, id, previousAnswer);

    updateAnswers(previousAnswers => ({
      ...(previousAnswers || {}),
      [id]: optimisticAnswer
    }));

    try {
      const saveAnswerVariables: SaveAnswerVariables = {
        formId: INTAKE_FORM_ID,
        formElementId: parseInt(id)
      };
      if (Array.isArray(answerValue)) {
        saveAnswerVariables.dataArray = answerValue;
      } else {
        saveAnswerVariables.data = answerValue;
      }

      const savedAnswer = await saveAnswer({
        variables: saveAnswerVariables
      });

      if (savedAnswer.data?.createPatientFormAnswer?.answer) {
        const answer = savedAnswer.data.createPatientFormAnswer.answer;
        setProgress(savedAnswer.data?.createPatientFormAnswer?.percentageComplete);
        updateAnswers(previousAnswers => ({
          ...(previousAnswers || {}),
          [answer.formElementId]: answer
        }));
      } else if (
        savedAnswer.data?.createPatientFormAnswer?.errors === 'FORM_ALREADY_COMPLETED' ||
        savedAnswer.data?.createPatientFormAnswer?.errors === 'FORM_NOT_AVAILABLE'
      ) {
        restorePreviousAnswer(id, previousAnswer);
        onFormBecomeUnavailable();
      } else if (
        savedAnswer.data?.createPatientFormAnswer?.errors &&
        savedAnswer.data?.createPatientFormAnswer?.errors !== 'DUPLICATE_FORM_COMPLETION'
      ) {
        restorePreviousAnswer(id, previousAnswer);
        // If request to save an answer, resulted with duplicate form_completions, we won't
        // bother patient with that. In other cases, we will log the error in HB.
        BugTracker.notify(savedAnswer.data.createPatientFormAnswer.errors, 'IntakeFormSaveAnswer');
      }
    } catch (e) {
      restorePreviousAnswer(id, previousAnswer);
      BugTracker.notify(e, 'IntakeFormSaveAnswer');
      showErrorToast('Failed to save the answer. Please try again.');
    }
  };

  const onSubmitForm = async () => {
    try {
      const formCompletionResponse = await completeIntakeForm({
        variables: { formId: INTAKE_FORM_ID, completedAt: moment().toISOString() }
      });
      if (formCompletionResponse.data?.updatePatientFormCompletion.formCompletion != null) {
        navigate(pageUrl.dashboard());
      } else if (
        formCompletionResponse.data?.updatePatientFormCompletion.errors ===
          'FORM_ALREADY_COMPLETED' ||
        formCompletionResponse.data?.updatePatientFormCompletion.errors === 'FORM_NOT_AVAILABLE'
      ) {
        onFormBecomeUnavailable();
      } else if (formCompletionResponse.data?.updatePatientFormCompletion.errors != null) {
        reportFormCompletingError(formCompletionResponse.data?.updatePatientFormCompletion.errors);
      }
    } catch (e) {
      reportFormCompletingError(e);
    }
  };

  const renderForm = () => {
    if (answers == null) return null;

    return (
      <FormRenderer
        ref={formRendererRef}
        formData={formStructure}
        questionsToHide={getQuestionIdsToHide(
          formStructure.formSections[currentFormSectionIndex],
          formStructure.conditionalFormElements || [],
          answers
        ).concat(invalidQuestions)}
        currentSection={currentFormSectionIndex}
        currentForm={currentFormIndex}
        topOffset={HEADER_FOOTER_HEIGHT}
        showNextSection={showNextSection}
        showPreviousSection={showPreviousSection}
        onSubmitAnswer={(response: string | Array<string> | undefined, id: string) => {
          submitAnswer(response, id);
        }}
        savedAnswers={answers}
        onSubmitForm={onSubmitForm}
      />
    );
  };

  const showSelectedSection = (selectedSection: FormSection) => {
    const currSection = currentSection();
    const formElementWithMissingAnswer = getFirstRequiredFormElementMissingAnswer(currSection);
    if (formElementWithMissingAnswer) {
      notifyAboutMissingAnswer(formElementWithMissingAnswer);
      return;
    }

    const { formSections } = formStructure;
    const formSectionToShowIndex = formSections.findIndex(
      formSection => formSection.position === selectedSection.position
    );

    if (formSectionToShowIndex === -1) return;

    updateDisplayedForm(formSectionToShowIndex, 0);
  };

  const currentSectionIndex = currentFormSectionIndex + 1;
  const totalSections = formStructure.formSections.length;
  const currentFormSection = currentSection();
  const currentFormSectionTitle = currentFormSection?.title || '';

  if (loadingForm || answers == null || formStructure.id === '') return <Loader absoluteCentered />;

  return (
    <Container>
      <BackgroundColor />
      <StickyHeader>
        <Header
          sectionTitle={currentFormSectionTitle}
          currentSectionIndex={currentSectionIndex}
          totalSections={totalSections}
          percentageComplete={progress}
          sections={formStructure.formSections}
          selectedSection={currentFormSection}
          onSectionSelected={newSelectedSection => showSelectedSection(newSelectedSection)}
        />
      </StickyHeader>
      <Content>
        <SectionContainer>
          <SectionIndicator sections={totalSections} selectedSection={currentFormSectionIndex} />
        </SectionContainer>
        <FormContainer>{renderForm()}</FormContainer>
      </Content>
      <StickyFooter>
        <ProgressFooter percentComplete={calculateProgress()} />
      </StickyFooter>
    </Container>
  );
};

const reportFormCompletingError = (error: unknown) => {
  let errorMessage = `Sorry, we aren't able to complete the form. Please try again or contact us at ${KBContacts.navigatorEmail}`;
  if (error instanceof Error) {
    if (error.message.includes('Please fill out all of the required fields')) {
      errorMessage = 'Please fill out all of the required fields.';
    } else if (error.message.includes('Form completion could not be found')) {
      errorMessage = 'Please fill out all of the required fields.';
    } else {
      BugTracker.notify(error, 'IntakeFormSubmit');
    }
  } else {
    BugTracker.notify(error, 'IntakeFormSubmit');
  }

  showErrorToast(errorMessage);
};
